import type { UserPermissionWithPath } from 'api';
import { firmIntelligenceUrl } from 'common/config/urls';
import { PORTFOLIO_VALUATION } from 'common/constants/auth';
import { FUND_PERMISSION_NAME } from 'common/constants/sidebar';
import { COMPANY_NAME_TITLE } from 'common/constants/summary';
import { dashboardCompanyOverviewUrl, WORKBOARD_CURRENT_VALUATIONS_URL } from 'dashboard409a/common/config/urls';
import { getPreviousWorkboardRoute } from 'dashboard409a/utilities/navigation';
import type { CompanySelectorPermission } from 'pages/Auth/components/CompanySelector/CompanySelector.props';
import type { LoginFirm } from 'pages/Auth/components/FirmList';
import { clearAppAutoSelect, getAppAutoSelect, getSafePreviousPortfolioRoute } from 'pages/Auth/utils';
import type { WorkspaceLocationState, WorkspaceRedirectUser } from './useWorkspaceAutoRedirects.types';

interface ResolveWorkspaceAutoRedirectParams {
  hasNotFirmPermissions: boolean;
  isCompanyProduct: boolean;
  locationState?: WorkspaceLocationState | null;
  lowerPermissions: UserPermissionWithPath[];
  resolvedFirmList: LoginFirm[] | null;
  resolvedStandaloneCompaniesPermissions: CompanySelectorPermission[];
  resolvedUser?: WorkspaceRedirectUser;
}

export type WorkspaceAutoRedirectPlan =
  | {
    kind: 'clear-app-auto-select';
  }
  | {
    kind: 'deep-link' | 'portfolio-resume' | 'single-firm' | 'single-company' | 'staff-company';
    target: string;
  }
  | {
    kind: 'lower-permissions';
    permission: UserPermissionWithPath;
    target: string;
  };

const getSelectedLowerPermission = (lowerPermissions: UserPermissionWithPath[]) => {
  // Restricted users can have multiple lower permissions when they have access to a fund
  // and the companies in which that fund invested; prefer the fund route in that case.
  const fundPermission = lowerPermissions.find(
    ({ feature_object }) => feature_object?.object_type === FUND_PERMISSION_NAME
  );

  return lowerPermissions.length > 1 && fundPermission ? fundPermission : lowerPermissions[0];
};

const getPathname = (route: string) => route.split(/[?#]/)[0] ?? '';

const normalizePath = (route: string) => getPathname(route).replace(/^\/+/, '').replace(/\/+$/, '');

const getFirmSlugFromPortfolioRoute = (route: string) => {
  const [root, firmSlug] = normalizePath(route).split('/');

  return root === 'firms' ? firmSlug || null : null;
};

const getPermissionObjectBasePath = (permissionPath?: string) => {
  if (!permissionPath) {
    return null;
  }

  const segments = normalizePath(permissionPath).split('/');
  const objectPathIndex = segments.findIndex(segment => segment === 'companies' || segment === 'funds');

  if (objectPathIndex === -1) {
    return normalizePath(permissionPath);
  }

  const objectSlug = segments[objectPathIndex + 1];

  if (!objectSlug) {
    return null;
  }

  return segments.slice(0, objectPathIndex + 2).join('/');
};

const userCanAccessLowerPermissionRoute = (
  route: string,
  lowerPermissions: UserPermissionWithPath[]
) => {
  const routePath = normalizePath(route);

  return lowerPermissions.some(permission => {
    const permissionObjectBasePath = getPermissionObjectBasePath(permission.feature_object_path);

    return permissionObjectBasePath
      ? routePath === permissionObjectBasePath || routePath.startsWith(`${permissionObjectBasePath}/`)
      : false;
  });
};

const userCanAccessSavedPortfolioRoute = ({
  lowerPermissions,
  resolvedFirmList,
  resolvedUser,
  route,
}: {
  lowerPermissions: UserPermissionWithPath[];
  resolvedFirmList: LoginFirm[] | null;
  resolvedUser?: WorkspaceRedirectUser;
  route: string;
}) => {
  const firmSlug = getFirmSlugFromPortfolioRoute(route);

  const matchingFirm = resolvedFirmList?.find(firm => firm.slug === firmSlug);

  if (matchingFirm && (resolvedUser?.is_superuser || (matchingFirm.user_permissions?.length ?? 0) > 0)) {
    return true;
  }

  return userCanAccessLowerPermissionRoute(route, lowerPermissions);
};

export const applyWorkspaceAutoRedirectPlanSideEffects = async ({
  fetchCompanyInfo,
  fetchFundInfo,
  markRestrictedUser,
  plan,
}: {
  fetchCompanyInfo: (companyId: number) => Promise<void> | void;
  fetchFundInfo: (fundId: number) => Promise<void> | void;
  markRestrictedUser: () => void;
  plan: WorkspaceAutoRedirectPlan;
}) => {
  if (plan.kind === 'clear-app-auto-select') {
    clearAppAutoSelect();
    return;
  }

  if (plan.kind !== 'lower-permissions') {
    clearAppAutoSelect();
    return;
  }

  const featureObject = plan.permission.feature_object;

  // Downstream firm/company containers still rely on this global restricted-user flag
  // and the matching object info being hydrated before navigation.
  markRestrictedUser();

  try {
    if (featureObject?.object_type === COMPANY_NAME_TITLE) {
      await fetchCompanyInfo(featureObject.object_id);
    } else if (featureObject?.object_id) {
      await fetchFundInfo(featureObject.object_id);
    }
  } finally {
    clearAppAutoSelect();
  }
};

export const resolveWorkspaceAutoRedirectPlan = ({
  hasNotFirmPermissions,
  isCompanyProduct,
  locationState,
  lowerPermissions,
  resolvedFirmList,
  resolvedStandaloneCompaniesPermissions,
  resolvedUser,
}: ResolveWorkspaceAutoRedirectParams): WorkspaceAutoRedirectPlan | null => {
  const pathname = locationState?.from?.pathname;
  const search = locationState?.from?.search;

  if (pathname && search) {
    return {
      kind: 'deep-link',
      target: `${pathname}${search}`,
    };
  }

  if (!isCompanyProduct && getAppAutoSelect() === PORTFOLIO_VALUATION) {
    const resume = getSafePreviousPortfolioRoute(resolvedUser?.id);

    // Saved routes come from localStorage, so permissions must be checked again
    // before they can outrank deterministic single-option routing.
    if (resume && userCanAccessSavedPortfolioRoute({
      lowerPermissions,
      resolvedFirmList,
      resolvedUser,
      route: resume,
    })) {
      return {
        kind: 'portfolio-resume',
        target: resume,
      };
    }
  }

  if (!isCompanyProduct && !hasNotFirmPermissions && resolvedFirmList?.length === 1) {
    const firmSlug = resolvedFirmList[0]?.slug;

    if (firmSlug) {
      return {
        kind: 'single-firm',
        target: firmIntelligenceUrl(firmSlug),
      };
    }
  }

  if (isCompanyProduct && resolvedStandaloneCompaniesPermissions.length === 1) {
    const companySlug = resolvedStandaloneCompaniesPermissions[0]?.slug;

    if (companySlug) {
      return {
        kind: 'single-company',
        target: dashboardCompanyOverviewUrl(companySlug),
      };
    }
  }

  if (isCompanyProduct && resolvedUser?.is_staff) {
    // Only redirect staff to Workboard in the Company Valuations flow; Portfolio Valuation
    // users should continue to the firm workspace selector instead.
    return {
      kind: 'staff-company',
      target: getPreviousWorkboardRoute() || WORKBOARD_CURRENT_VALUATIONS_URL,
    };
  }

  if (hasNotFirmPermissions && lowerPermissions.length) {
    const permission = getSelectedLowerPermission(lowerPermissions);
    const target = permission?.feature_object_path;

    if (permission && target) {
      return {
        kind: 'lower-permissions',
        permission,
        target: `/${target}`,
      };
    }
  }

  return null;
};
